import {useAssignBasisMutation} from '@transactions/graphql';
import Money from 'common/Money';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import {InboxCard} from 'src/InboxCard';
import {formatListDate} from 'src/lib/displayDate';
import type {UnifiedTransactionFragment} from '@transactions/graphql';

// Enough rows to see the shape of the problem without taking over the inbox.
const VISIBLE_ROWS = 4;

const Row = ({
  transaction,
  onAssign,
  assigning,
}: {
  transaction: UnifiedTransactionFragment;
  onAssign: () => void;
  assigning: boolean;
}) => (
  <div className="border-border -mx-4 grid grid-cols-[1fr_auto] items-center gap-x-3 border-t px-4 py-2.5">
    <Link
      to={`/transactions/${transaction._id}`}
      className="min-w-0 hover:underline"
    >
      <span className="block truncate text-sm font-medium">
        {transaction.payee ?? transaction.description}
      </span>
      <span className="text-muted-foreground text-xs">
        {formatListDate(transaction.date)}
      </span>
    </Link>
    <span className="flex items-center gap-2">
      <span className="text-sm tabular-nums">
        {/* A transfer's own amount is 0 — it moved units without spending
            them, and what it lacks is the cost behind them. */}
        {transaction.netAmount !== 0 && (
          <AmountDisplay
            variant="plain"
            amount={Money.fromInt(transaction.netAmount, transaction.currency)}
          />
        )}
      </span>
      <Button variant="outline" onClick={onAssign} disabled={assigning}>
        Assign
      </Button>
    </span>
  </div>
);

/**
 * Foreign transactions that have no USD value yet, because no conversion has
 * supplied basis for them to draw.
 *
 * This is a to-do, not an error: entering a purchase before the withdrawal
 * that funded it is the normal order of things when you're back from a trip
 * with a stack of receipts. The card exists so those rows are visible rather
 * than silently missing from every total — reports omit them, since guessing
 * a rate would be wrong by the whole exchange rate.
 *
 * "Assign" draws basis from whatever conversions exist now. If none do, the
 * row stays put and the hint explains what's missing.
 */
type Props = {
  /**
   * Owned by InboxPage, not queried here: the page's "All caught up!" verdict
   * has to come from the same data as the cards it sits above, or an empty
   * state renders over actionable work. (That happened — the card queried for
   * itself, so the page's job count could not see its rows.)
   */
  rows: UnifiedTransactionFragment[];
  hasMore: boolean;
  /** Ask the page to raise its query limit by one page. */
  onShowMore: () => void;
  /** Refetch after an assignment lands. */
  onChanged: () => void;
};

export const InboxNeedsBasisCard = ({
  rows,
  hasMore,
  onShowMore,
  onChanged,
}: Props) => {
  const [assignBasis] = useAssignBasisMutation();
  const [expanded, setExpanded] = React.useState(false);
  const [assigningId, setAssigningId] = React.useState<string | null>(null);
  // Set when an assignment leaves the transaction unvalued: there was no
  // conversion to draw from, so the fix is recording one, not retrying.
  const [noBasisAvailable, setNoBasisAvailable] = React.useState(false);
  // The server refuses to re-pick a transfer's draws once its destination lot
  // has funded something. Without showing this the button just resets and the
  // row sits there, which reads as the app ignoring the click.
  const [assignError, setAssignError] = React.useState<string | null>(null);

  if (rows.length === 0) return null;

  const visible = expanded ? rows : rows.slice(0, VISIBLE_ROWS);
  const hiddenCount = rows.length - visible.length;

  const handleAssign = async (transactionId: string) => {
    setAssigningId(transactionId);
    setAssignError(null);
    try {
      const result = await assignBasis({variables: {transactionId}});
      setNoBasisAvailable(result.data?.assignBasis.needsBasis === true);
      onChanged();
    } catch (error) {
      setAssignError(
        error instanceof Error ? error.message : 'Could not assign basis.',
      );
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <InboxCard
      title="Transactions needing a US dollar value"
      meta={`${rows.length}${hasMore ? '+' : ''}`}
    >
      {visible.map((transaction) => (
        <Row
          key={transaction._id}
          transaction={transaction}
          assigning={assigningId === transaction._id}
          onAssign={() => void handleAssign(transaction._id)}
        />
      ))}
      {(hiddenCount > 0 || hasMore) && (
        <button
          type="button"
          className="text-muted-foreground border-border -mx-4 w-[calc(100%+2rem)] cursor-pointer border-t py-2.5 text-xs hover:underline"
          onClick={() => {
            setExpanded(true);
            if (hasMore) onShowMore();
          }}
        >
          {hiddenCount > 0 ? `Show ${hiddenCount} more` : 'Show more'}
        </button>
      )}
      {assignError !== null && (
        <div className="text-destructive border-border -mx-4 border-t px-4 py-2.5 text-xs">
          {assignError}
        </div>
      )}
      <div className="text-muted-foreground border-border -mx-4 border-t px-4 py-2.5 text-xs">
        {noBasisAvailable
          ? 'No conversion has any value left to draw from. Record the withdrawal or purchase that funded this currency, then assign again.'
          : 'Moved in a currency the app has no cost for yet. Record the conversion that funded it, then assign — a transfer listed here has to be assigned before anything spent at its destination can be.'}
      </div>
    </InboxCard>
  );
};
